import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { gqlRequest } from "../lib/gql";
import {
  activitiesToWire,
  DELETE_ROUTE,
  ROUTE_DETAIL,
  UPDATE_ROUTE,
  type RouteDetailResult,
  type UpdateRouteResult,
} from "../lib/queries";
import { MapPin, Pencil, Star, Trash2 } from "lucide-react";
import {
  ACTIVITIES,
  ACTIVITY_ICONS,
  formatDate,
  formatDistance,
  formatDuration,
  formatElevation,
  PLACE_TYPE_ICONS,
  type Activity,
} from "../lib/format";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { EntityTabs } from "../components/EntityTabs";
import { ActivityList, Chip, EmptyState, ErrorBox, Field, Spinner, Stars, TextInput } from "../components/ui";
import { Button } from "@/components/ui/button";
import { PhotoGallery, type GalleryPhoto } from "../components/PhotoGallery";
import { ReportButton } from "../components/ReportButton";
import { TickButton } from "../components/TickButton";
import { ViewOnMapButton } from "../components/ViewOnMapButton";
import { OutingCard } from "../components/OutingCard";
import { ElevationProfile } from "../components/ElevationProfile";
import { TrackExportButtons } from "../components/TrackExportButtons";
import { MiniMap } from "../map/MiniMap";
import { toFeature } from "../lib/geo";

export function RoutePage({ id }: { id: string }) {
  const { personId } = useAuth();
  const { t, labelizeT, entityName } = useT();
  const [editing, setEditing] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["route", id],
    queryFn: () => gqlRequest<RouteDetailResult>(ROUTE_DETAIL, { id }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;
  const route = data?.route;
  if (!route) return <EmptyState>{t("route.notFound")}</EmptyState>;

  const isOwner = personId != null && route.createdBy === personId;

  const mainTrack = route.routeTracksByRouteId.nodes.find((t) => t.role === "MAIN")?.track;
  const gradeEntries = Object.entries(route.grades ?? {});
  // Public ticks only — the query filters isPublic server-side, so the count
  // is the same for every viewer (the viewer's own public tick included).
  const tickCount = route.routeTicksByRouteId.totalCount;

  const header = (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-primary">{t("route.kicker")}</p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{entityName(route)}</h1>
        <div className="flex items-center gap-2">
          <ReportButton entityKind="ROUTE" entityId={route.id} />
          {isOwner && (
            <>
              <Button type="button" variant="outline" size="sm" className="text-xs" onClick={() => setEditing((v) => !v)}>
                <Pencil aria-hidden className="size-3.5" />
                {t("route.editDetails")}
              </Button>
              <DeleteRouteButton routeId={route.id} routeName={route.name} />
            </>
          )}
          <ViewOnMapButton geometry={route.mainGeom ?? route.startPoint} />
          <TickButton routeId={route.id} />
          <Button asChild>
            <Link href={`/new?routeId=${route.id}`}>{t("route.logOuting")}</Link>
          </Button>
        </div>
      </div>
      <p className="mt-1 flex flex-wrap items-center gap-x-1 text-sm text-muted-foreground">
        <ActivityList activities={route.activities} />
        {route.durationMin ? ` · ${formatDuration(route.durationMin)}` : ""}
        {route.orientation ? ` · ${route.orientation}` : ""}
      </p>
      {route.ratingCount > 0 && route.avgRating != null && (
        <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
          {/* Stars renders whole stars only, so round for the glyphs and keep
              the exact average in the text. */}
          <Stars rating={Math.round(Number(route.avgRating))} />
          {Number(route.avgRating).toFixed(1)}{" "}
          {t(route.ratingCount === 1 ? "route.ratingsOne" : "route.ratingsOther", { count: route.ratingCount })}
        </p>
      )}
      {tickCount > 0 && (
        <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
          <Star aria-hidden className="size-3.5 text-primary" fill="currentColor" />
          {t(tickCount === 1 ? "route.wantOne" : "route.wantOther", { count: tickCount })}
        </p>
      )}
      {route.createdByRow && (
        <p className="mt-1 text-sm text-muted-foreground">
          {t("route.addedBy")}{" "}
          <Link
            href={`/person/${route.createdByRow.id}`}
            className="font-medium text-primary transition-colors hover:underline"
          >
            {route.createdByRow.displayName ?? t("route.aMember")}
          </Link>
        </p>
      )}
      {isOwner && editing && (
        <EditRouteForm
          key={route.updatedAt}
          route={{ id: route.id, name: route.name, activities: route.activities, durationMin: route.durationMin }}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );

  const infoTab = (
    <div className="space-y-4">
      {route.description && <p className="whitespace-pre-line text-foreground">{route.description}</p>}
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {mainTrack && (
          <>
            <Stat label={t("stat.distance")} value={formatDistance(mainTrack.distanceM)} />
            <Stat label={t("stat.ascent")} value={formatElevation(mainTrack.ascentM)} />
            <Stat label={t("stat.descent")} value={formatElevation(mainTrack.descentM)} />
            <Stat label={t("stat.minElevation")} value={formatElevation(mainTrack.elevationMinM)} />
            <Stat label={t("stat.maxElevation")} value={formatElevation(mainTrack.elevationMaxM)} />
          </>
        )}
        {route.durationMin != null && <Stat label={t("stat.duration")} value={formatDuration(route.durationMin)} />}
        {gradeEntries.map(([activity, grade]) => (
          <Stat key={activity} label={t("stat.grade", { activity: labelizeT(activity) })} value={grade} />
        ))}
      </dl>
      {route.routeRelationsByRouteId.nodes.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-semibold text-foreground">{t("route.related")}</h3>
          <ul className="space-y-1">
            {route.routeRelationsByRouteId.nodes.map((rel) => (
              <li key={rel.id}>
                <Link
                  href={`/route/${rel.relatedRoute.id}`}
                  className="text-sm text-primary transition-colors hover:underline"
                >
                  {entityName(rel.relatedRoute)}
                </Link>{" "}
                <span className="text-xs text-muted-foreground">({labelizeT(rel.type)})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const mapTab = (
    <div className="space-y-3">
      <MiniMap
        features={[
          toFeature(route.mainGeom, { id: route.id }),
          toFeature(route.startPoint, { id: `${route.id}-start` }),
        ]}
        className="h-96 w-full overflow-hidden rounded-xl border shadow-card"
      />
      {mainTrack?.geom && (
        <TrackExportButtons
          trackId={mainTrack.id}
          geom={mainTrack.geom}
          pointTimes={mainTrack.pointTimes}
          name={route.name}
        />
      )}
      {/* Raw track geom keeps its Z coords; mainGeom is 2D after simplification. */}
      <ElevationProfile geom={mainTrack?.geom} pointTimes={mainTrack?.pointTimes} className="rounded-xl border bg-card p-3 text-card-foreground shadow-card" />
      {route.routeTracksByRouteId.nodes.length > 1 && (
        <ul className="space-y-1 text-sm text-muted-foreground">
          {route.routeTracksByRouteId.nodes.map((rt) => (
            <li key={rt.id}>
              <span className="font-medium">{labelizeT(rt.role)}</span>
              {rt.label ? ` — ${rt.label}` : ""} · {formatDistance(rt.track.distanceM)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const placesTab = route.routePlacesByRouteId.nodes.length ? (
    <ul className="divide-y overflow-hidden rounded-xl border bg-card text-card-foreground shadow-card">
      {route.routePlacesByRouteId.nodes.map((rp) => {
        const PlaceIcon = PLACE_TYPE_ICONS[rp.place.type] ?? MapPin;
        return (
        <li key={rp.id}>
          <Link
            href={`/place/${rp.place.id}`}
            className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-accent"
          >
            <PlaceIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1">
              <span className="block font-medium text-foreground">{entityName(rp.place)}</span>
              <span className="block text-xs text-muted-foreground">
                {labelizeT(rp.place.type)} · {labelizeT(rp.role)}
                {rp.place.elevation ? ` · ${rp.place.elevation} m` : ""}
              </span>
            </span>
          </Link>
        </li>
        );
      })}
    </ul>
  ) : (
    <EmptyState>{t("route.noPlaces")}</EmptyState>
  );

  const outings = route.outingRoutesByRouteId.nodes;

  // Community photos (F23): photos of outings logged on this route. RLS
  // scopes the outings (and so the photos) to what this viewer may see. The
  // document samples 20 outings x 4 photos; dedupe by photo id guards against
  // an outing appearing under several outing_route rows.
  const photoMap = new Map<string, GalleryPhoto>();
  let photosTruncated = route.outingRoutesByRouteId.totalCount > outings.length;
  for (const o of outings) {
    if (o.outing.photos.totalCount > o.outing.photos.nodes.length) photosTruncated = true;
    for (const p of o.outing.photos.nodes) {
      if (!photoMap.has(p.id)) {
        photoMap.set(p.id, {
          ...p,
          link: { href: `/outing/${o.outing.id}`, label: `${o.outing.title} · ${formatDate(o.outing.date)}` },
        });
      }
    }
  }
  const communityPhotos = [...photoMap.values()];

  const photosTab = communityPhotos.length ? (
    <div className="space-y-3">
      <PhotoGallery photos={communityPhotos} />
      {photosTruncated && (
        <p className="text-center text-xs text-muted-foreground">{t("route.photoSample")}</p>
      )}
    </div>
  ) : (
    <EmptyState>{t("route.noPhotos")}</EmptyState>
  );

  const outingsTab = outings.length ? (
    <div className="space-y-3">
      {outings.map((o) => (
        <OutingCard key={o.outing.id} outing={o.outing} />
      ))}
    </div>
  ) : (
    <EmptyState>
      {t("route.noOutings")}{" "}
      <Link
        href={`/new?routeId=${route.id}`}
        className="text-primary transition-colors hover:underline"
      >
        {t("route.beFirst")}
      </Link>
    </EmptyState>
  );

  return (
    <EntityTabs
      header={header}
      tabs={[
        { key: "info", label: t("tabs.info"), content: infoTab },
        { key: "map", label: t("tabs.map"), content: mapTab },
        { key: "photos", label: t("tabs.photos"), badge: communityPhotos.length, content: photosTab },
        { key: "places", label: t("tabs.places"), badge: route.routePlacesByRouteId.nodes.length, content: placesTab },
        { key: "outings", label: t("tabs.outings"), badge: route.outingRoutesByRouteId.totalCount, content: outingsTab },
      ]}
    />
  );
}

// Owner-only inline edit for the descriptive fields (F43). Deliberately
// small: name, activities, duration. Geometry re-drawing/editing is future
// work — changing the line means drawing a new route for now.
function EditRouteForm({
  route,
  onClose,
}: {
  route: { id: string; name: string; activities: Activity[]; durationMin: number | null };
  onClose: () => void;
}) {
  const { t, labelizeT } = useT();
  const queryClient = useQueryClient();
  const [name, setName] = useState(route.name);
  const [activities, setActivities] = useState<Activity[]>(route.activities);
  const [duration, setDuration] = useState(route.durationMin != null ? String(route.durationMin) : "");

  const save = useMutation({
    mutationFn: () =>
      gqlRequest<UpdateRouteResult>(UPDATE_ROUTE, {
        id: route.id,
        patch: {
          name: name.trim(),
          // Lowercase labels — see the TODO(pdbq) enum-array note in queries.ts.
          activities: activitiesToWire(activities),
          durationMin: duration.trim() === "" ? null : Math.round(Number(duration)),
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["route", route.id] });
      onClose();
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (name.trim() && activities.length > 0 && !save.isPending) save.mutate();
  }

  return (
    <form onSubmit={onSubmit} className="mt-3 space-y-3 rounded-xl border border-primary/30 bg-primary/5 p-4">
      <Field label={t("common.name")}>
        <TextInput required value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label={t("form.activities")} error={activities.length === 0 ? t("form.pickActivity") : undefined}>
        <div className="flex flex-wrap gap-2">
          {ACTIVITIES.map((a) => {
            const Icon = ACTIVITY_ICONS[a];
            const active = activities.includes(a);
            return (
              <Chip
                key={a}
                active={active}
                onClick={() => setActivities(active ? activities.filter((x) => x !== a) : [...activities, a])}
              >
                <span className="inline-flex items-center gap-1.5">
                  <Icon aria-hidden className="size-3.5" />
                  {labelizeT(a)}
                </span>
              </Chip>
            );
          })}
        </div>
      </Field>
      <Field label={t("form.durationOptional")}>
        <TextInput
          type="number"
          inputMode="numeric"
          min={1}
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
        />
      </Field>
      {save.error != null && <ErrorBox error={save.error} />}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending || !name.trim() || activities.length === 0}>
          {save.isPending ? t("common.saving") : t("common.save")}
        </Button>
        <Button type="button" variant="outline" onClick={onClose}>
          {t("common.cancel")}
        </Button>
      </div>
    </form>
  );
}

function deleteErrorMessage(err: unknown, fkMessage: string): string {
  const msg = err instanceof Error ? err.message : String(err);
  // FK RESTRICT (23503) from outing_route: outings logged on the route keep it
  // alive. GqlError only carries the message string, so match on it.
  if (/foreign key/i.test(msg) && /outing_route/.test(msg)) return fkMessage;
  return msg;
}

function DeleteRouteButton({ routeId, routeName }: { routeId: string; routeName: string }) {
  const { t } = useT();
  const [, navigate] = useLocation();
  const del = useMutation({
    mutationFn: () => gqlRequest(DELETE_ROUTE, { id: routeId }),
    // The route's linked track rows cascade; the track itself stays (there is
    // no working deleteTrack), which is acceptable — orphan tracks are unlisted.
    onSuccess: () => navigate("/"),
  });

  return (
    <span className="inline-flex flex-col items-end">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => {
          if (window.confirm(t("route.confirmDelete", { name: routeName }))) del.mutate();
        }}
        disabled={del.isPending}
        className="text-xs text-destructive hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive"
      >
        <Trash2 aria-hidden className="size-3.5" />
        {del.isPending ? t("common.deleting") : t("common.delete")}
      </Button>
      {del.error != null && (
        <span className="mt-1 max-w-64 text-right text-xs text-destructive">
          {deleteErrorMessage(del.error, t("route.fkDelete"))}
        </span>
      )}
    </span>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-muted/50 p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-base font-semibold text-foreground">{value}</dd>
    </div>
  );
}
